import { push } from 'notivue';
import type { NotificationClearMethods, PushPromiseReturn } from 'notivue';
import { shallowReactive, shallowRef } from 'vue';
import type { ShallowRef } from 'vue';

import type {
  AppBlockerInput,
  AppNotificationAction,
  AppNotificationAriaLive,
  AppNotificationContent,
  AppNotificationOptions,
  AppNotificationRenderProps,
  AppNotifications,
  AppNotificationRole,
  AppNotificationTone,
  AppPromiseMessages,
  NotificationHandle
} from './appNotificationTypes';

export const APP_NOTIFICATION_DURATION = {
  success: 5_500,
  info: 6_000,
  warning: 9_000,
  error: 10_000
} as const;

const ACTION_ERROR_TITLE = 'Действие не выполнено';
const ACTION_ERROR_MESSAGE = 'Не удалось выполнить действие. Попробуйте ещё раз.';
const PROMISE_SUCCESS_FALLBACK = { message: 'Операция выполнена.' };
const PROMISE_ERROR_FALLBACK = {
  title: 'Операция не выполнена',
  message: 'Попробуйте ещё раз.'
};

type StaticNotificationType = 'success' | 'info' | 'warning' | 'error';

interface StaticNotificationSpec {
  tone: AppNotificationTone;
  role: AppNotificationRole;
  ariaLive: AppNotificationAriaLive;
  duration: number;
}

interface BlockerRegistryEntry {
  renderProps: AppNotificationRenderProps;
  title: ShallowRef<string>;
  message: ShallowRef<string>;
  handle: NotificationHandle;
}

type PromiseNotificationHandle = PushPromiseReturn & NotificationClearMethods;

const STATIC_NOTIFICATION_SPECS: Record<StaticNotificationType, StaticNotificationSpec> = {
  success: {
    tone: 'success',
    role: 'status',
    ariaLive: 'polite',
    duration: APP_NOTIFICATION_DURATION.success
  },
  info: {
    tone: 'info',
    role: 'status',
    ariaLive: 'polite',
    duration: APP_NOTIFICATION_DURATION.info
  },
  warning: {
    tone: 'warning',
    role: 'status',
    ariaLive: 'polite',
    duration: APP_NOTIFICATION_DURATION.warning
  },
  error: {
    tone: 'danger',
    role: 'alert',
    ariaLive: 'assertive',
    duration: APP_NOTIFICATION_DURATION.error
  }
};

const blockerRegistry = new Map<string, BlockerRegistryEntry>();

const normalizeContent = (
  content: string | AppNotificationContent
): AppNotificationContent =>
  typeof content === 'string' ? { message: content } : content;

const resolvePromiseContent = <T>(
  value:
    | string
    | AppNotificationContent
    | ((result: T) => string | AppNotificationContent),
  result: T
) => normalizeContent(typeof value === 'function' ? value(result) : value);

const createRenderProps = (
  content: AppNotificationContent,
  spec: Pick<StaticNotificationSpec, 'tone' | 'role' | 'ariaLive'>,
  options: {
    action?: AppNotificationAction;
    closable?: boolean;
    reasons?: string[];
    hiddenReasonCount?: number;
    repeatCount?: number;
  } = {}
) =>
  shallowReactive<AppNotificationRenderProps>({
    title: content.title ?? '',
    message: content.message,
    tone: spec.tone,
    role: spec.role,
    ariaLive: spec.ariaLive,
    reasons: options.reasons ?? [],
    hiddenReasonCount: options.hiddenReasonCount ?? 0,
    repeatCount: options.repeatCount ?? 1,
    action: options.action,
    closable: options.closable ?? true
  });

const createNotivueTextRef = (value: string) => {
  const text = shallowRef(value);

  // Notivue 2.4.5 supports Ref text, but its keyboard label stringifies the
  // Ref object. Keep that label reactive while returning the current value.
  Object.defineProperty(text, Symbol.toPrimitive, {
    value: () => text.value
  });

  return text;
};

function pushStaticNotification(
  type: StaticNotificationType,
  message: string,
  options: AppNotificationOptions = {}
): void {
  const spec = STATIC_NOTIFICATION_SPECS[type];
  const renderProps = createRenderProps(
    { title: options.title, message },
    spec,
    {
      action: options.action,
      closable: options.closable
    }
  );
  renderProps.onActionError = () => {
    pushStaticNotification(
      'error',
      renderProps.action?.errorMessage ?? ACTION_ERROR_MESSAGE,
      { title: ACTION_ERROR_TITLE }
    );
  };
  const payload = {
    title: renderProps.title,
    message: renderProps.message,
    duration: options.action ? Infinity : (options.duration ?? spec.duration),
    ariaRole: spec.role,
    ariaLive: spec.ariaLive,
    props: renderProps
  };

  switch (type) {
    case 'success':
      push.success<AppNotificationRenderProps>(payload);
      return;
    case 'info':
      push.info<AppNotificationRenderProps>(payload);
      return;
    case 'warning':
      push.warning<AppNotificationRenderProps>(payload);
      return;
    case 'error':
      push.error<AppNotificationRenderProps>(payload);
  }
}

const normalizeReasons = (reasons: string[] | undefined) =>
  (reasons ?? []).map((reason) => reason.trim()).filter(Boolean);

const releaseBlocker = (key: string, entry: BlockerRegistryEntry) => {
  if (blockerRegistry.get(key) === entry) {
    blockerRegistry.delete(key);
  }
};

const pushBlockerNotification = (
  input: AppBlockerInput,
  key: string,
  reasons: string[],
  repeatCount: number
): NotificationHandle => {
  const renderProps = createRenderProps(
    { title: input.title, message: input.message },
    { tone: 'blocker', role: 'alert', ariaLive: 'assertive' },
    {
      action: input.action,
      closable: input.closable,
      reasons,
      repeatCount
    }
  );
  renderProps.onActionError = () => {
    pushStaticNotification(
      'error',
      renderProps.action?.errorMessage ?? ACTION_ERROR_MESSAGE,
      { title: ACTION_ERROR_TITLE }
    );
  };
  const title = createNotivueTextRef(renderProps.title);
  const message = createNotivueTextRef(renderProps.message);
  const entry = {
    renderProps,
    title,
    message,
    handle: {
      clear: () => undefined,
      destroy: () => undefined
    }
  };
  const cleanup = () => releaseBlocker(key, entry);
  renderProps.onBeforeClear = cleanup;
  const notivueHandle = push.error<AppNotificationRenderProps>({
    title,
    message,
    duration: Infinity,
    ariaRole: 'alert',
    ariaLive: 'assertive',
    skipQueue: true,
    props: renderProps,
    onAutoClear: cleanup,
    onManualClear: cleanup
  });

  entry.handle = {
    clear: () => {
      cleanup();
      notivueHandle.clear();
    },
    destroy: () => {
      cleanup();
      notivueHandle.destroy();
    }
  };
  blockerRegistry.set(key, entry);

  return entry.handle;
};

const createBlocker = (input: AppBlockerInput): NotificationHandle => {
  const key = input.key.trim();
  if (!key) {
    throw new Error('Blocker notification key must not be empty.');
  }

  const reasons = normalizeReasons(input.reasons);
  const existing = blockerRegistry.get(key);

  if (existing) {
    const repeatCount = existing.renderProps.repeatCount + 1;
    existing.title.value = input.title;
    existing.message.value = input.message;
    existing.renderProps.title = input.title;
    existing.renderProps.message = input.message;
    existing.renderProps.reasons = reasons;
    existing.renderProps.hiddenReasonCount = 0;
    existing.renderProps.repeatCount = repeatCount;
    existing.renderProps.action = input.action;
    existing.renderProps.closable = input.closable ?? true;
    return existing.handle;
  }

  return pushBlockerNotification(input, key, reasons, 1);
};

const pushPromiseState = (
  handle: PushPromiseReturn,
  type: 'success' | 'error',
  content: AppNotificationContent
): NotificationClearMethods => {
  const spec = STATIC_NOTIFICATION_SPECS[type];
  const renderProps = createRenderProps(content, spec);
  const payload = {
    title: renderProps.title,
    message: renderProps.message,
    duration: spec.duration,
    ariaRole: spec.role,
    ariaLive: spec.ariaLive,
    props: renderProps
  };

  return type === 'success'
    ? handle.resolve<AppNotificationRenderProps>(payload)
    : handle.reject<AppNotificationRenderProps>(payload);
};

const resolvePromiseContentSafely = <T>(
  value:
    | string
    | AppNotificationContent
    | ((result: T) => string | AppNotificationContent),
  result: T,
  fallback: AppNotificationContent
) => {
  try {
    return resolvePromiseContent(value, result);
  } catch (error) {
    console.error('Failed to format notification content', error);
    return fallback;
  }
};

const pushPromiseStateSafely = (
  handle: PromiseNotificationHandle,
  type: 'success' | 'error',
  content: AppNotificationContent
) => {
  try {
    pushPromiseState(handle, type, content);
  } catch (error) {
    console.error('Failed to update promise notification', error);
    try {
      handle.destroy();
    } catch (cleanupError) {
      console.error('Failed to remove promise notification', cleanupError);
    }
  }
};

const trackPromise = async <T>(
  operation: Promise<T>,
  messages: AppPromiseMessages<T>
): Promise<T> => {
  const loadingContent = normalizeContent(messages.loading);
  const loadingRenderProps = createRenderProps(
    loadingContent,
    { tone: 'info', role: 'status', ariaLive: 'polite' },
    { closable: false }
  );
  const handle = push.promise<AppNotificationRenderProps>({
    title: loadingRenderProps.title,
    message: loadingRenderProps.message,
    ariaRole: 'status',
    ariaLive: 'polite',
    props: loadingRenderProps
  });

  let result: T;
  try {
    result = await operation;
  } catch (error) {
    pushPromiseStateSafely(
      handle,
      'error',
      resolvePromiseContentSafely(
        messages.error,
        error,
        PROMISE_ERROR_FALLBACK
      )
    );
    throw error;
  }

  pushPromiseStateSafely(
    handle,
    'success',
    resolvePromiseContentSafely(
      messages.success,
      result,
      PROMISE_SUCCESS_FALLBACK
    )
  );
  return result;
};

const appNotifications: AppNotifications = {
  success: (message, options) =>
    pushStaticNotification('success', message, options),
  info: (message, options) =>
    pushStaticNotification('info', message, options),
  warning: (message, options) =>
    pushStaticNotification('warning', message, options),
  error: (message, options) =>
    pushStaticNotification('error', message, options),
  blocker: createBlocker,
  promise: trackPromise
};

export const useAppNotifications = (): AppNotifications => appNotifications;
